import type { ModalBorrowingSource } from './borrowing.js'
import type { ChordBorrowing, ChordFunction } from './types.js'

interface DescribeInput {
  chordFunction: ChordFunction
  borrowing: ChordBorrowing
  /**
   * True for a dominant-7 quality chord (B♭7, B♭9). Selects the seventh-chord
   * text where it differs from the triad's (see `DOMINANT_SEVENTH_TEMPLATES`).
   */
  isDominantSeventh?: boolean
  /**
   * The parallel mode a borrowed chord comes from, when it is only in the
   * parallel Mixolydian or Dorian (GH #2147). Named in the description
   * ("Borrowed from C Mixolydian.") ahead of every other template.
   */
  modalSource?: { mode: ModalBorrowingSource; tonic: string }
}

const MODE_DISPLAY_NAMES: Record<ModalBorrowingSource, string> = {
  mixolydian: 'Mixolydian',
  dorian: 'Dorian',
}

/**
 * Look up a one-line rationale (≤60 chars) for a (chordFunction, borrowing)
 * combination. These templates are owned by Bowie and reviewed by Joni.
 */
const TEMPLATES: Record<string, string> = {
  // Major-scale diatonic
  'I|null': 'Tonic — home.',
  'ii|null': 'Supertonic — predominant.',
  'iii|null': 'Mediant — passing color.',
  'IV|null': 'Subdominant — predominant motion.',
  'V|null': 'Dominant — resolves to tonic.',
  'vi|null': 'Submediant — relative-minor home.',
  'vii°|null': 'Leading tone — sharp pull to tonic.',

  // Minor-scale diatonic
  'i|null': 'Tonic — home.',
  'ii°|null': 'Supertonic — predominant.',
  '♭III|null': 'Mediant — relative-major home.',
  'iv|null': 'Subdominant — predominant motion.',
  'v|null': 'Dominant — weak pull (no leading tone).',
  '♭VI|null': 'Submediant — lifted color.',
  '♭VII|null': 'Subtonic — natural minor.',

  // Minor-flavored mode diatonic chords (Dorian / Phrygian / Mixolydian / Locrian).
  // Bowie-owned register: `<Role> — <context>.`
  'i°|null': 'Tonic diminished — Locrian center.',
  'iii°|null': 'Mediant diminished — Mixolydian color.',
  'v°|null': 'Dominant diminished — Phrygian color.',
  'vi°|null': 'Submediant diminished — Dorian color.',
  '♭iii|null': 'Mediant — Locrian.',
  '♭V|null': 'Major triad on flat-five.',
  '♭vii|null': 'Subtonic minor — Phrygian color.',

  // Lydian-distinctive diatonic chords (v1.5b).
  'II|null': 'Supertonic major — Lydian color.',
  '#iv°|null': 'Raised-fourth diminished — Lydian characteristic.',
  'vii|null': 'Leading-tone minor — Lydian.',

  // Harmonic-minor-distinctive diatonic chord (v1.5c).
  '♭III+|null': 'Augmented mediant — harmonic minor.',

  // Parallel-mode borrowings (chordFunction may also appear in minor-diatonic
  // above; the borrowing field disambiguates).
  '♭III|parallel-mode': 'Borrowed from parallel minor.',
  'iv|parallel-mode': 'Minor subdominant — borrowed from parallel minor.',
  '♭VI|parallel-mode': 'Borrowed from parallel minor.',
  '♭VII|parallel-mode': 'Borrowed ♭VII — Mixolydian color.',
  '♭II|parallel-mode': 'Neapolitan — Phrygian color.',
  // Half-diminished on the raised 7th in a minor key (D♯m7b5 in E minor).
  'vii°|parallel-mode': 'Leading-tone half-diminished — from the parallel major.',
  // (When borrowing into minor from parallel major)
  'IV|parallel-mode': 'Major subdominant — borrowed from parallel major.',

  // Secondary dominants
  'V/ii|secondary-dominant': 'Secondary dominant of ii.',
  'V/IV|secondary-dominant': 'Secondary dominant of IV.',
  'V/V|secondary-dominant': 'Secondary dominant of V.',
  'V/vi|secondary-dominant': 'Secondary dominant of vi.',
  // Minor keys (GH #2147)
  'V/iv|secondary-dominant': 'Secondary dominant of iv.',
  'V/♭VI|secondary-dominant': 'Secondary dominant of ♭VI.',
  'V/♭VII|secondary-dominant': 'Secondary dominant of ♭VII.',

  // Tritone substitutions (jazz, v1.5e). Borrowing is null because subV is a
  // chord-function category in its own right — distinct from `secondary-
  // dominant` (which preserves the leading-tone pull) and from `parallel-
  // mode` (modal-interchange borrowing). Each label resolves to its target
  // by half-step descent, the same b6→5 / b2→1 motion the original V would
  // have given by perfect-fifth descent.
  'subV/I|null': 'Tritone sub for V — resolves to tonic.',
  'subV/ii|null': 'Tritone sub for V/ii — resolves to ii.',
  'subV/IV|null': 'Tritone sub for V/IV — resolves to IV.',
  'subV/V|null': 'Tritone sub for V/V — resolves to V.',
  'subV/vi|null': 'Tritone sub for V/vi — resolves to vi.',

  // Chords with no third (sus, power) whose tones are all in the key
  'open|null': 'Open chord (no third), in the key.',

  // Chromatic outliers
  'chromatic|null': 'Outside the key.',
}

/**
 * Dominant-seventh overrides, keyed like `TEMPLATES`. The ♭VII7 borrowed from
 * the parallel minor (B♭7 in C) adds A♭, an Aeolian note, so it is the
 * backdoor dominant rather than Mixolydian color (Bowie, GH #2136). They win
 * over the jazz overrides, which describe the triad.
 */
const DOMINANT_SEVENTH_TEMPLATES: Record<string, string> = {
  '♭VII|parallel-mode': 'Borrowed from parallel minor — backdoor dominant to I.',
  // A dominant IV with an altered tone outside the parallel Dorian (F7♭9 in C,
  // A7♭9 in E minor). Must not fall through to the triad's "borrowed from
  // parallel major" text, which is wrong in a major key (Bowie, GH #2147).
  'IV|parallel-mode': 'Subdominant seventh — dominant-quality IV.',
}

/**
 * Jazz-style description overrides for parallel-mode borrowings. When the
 * analyzer is invoked with `style: 'jazz'` (`describeChord(input, { style:
 * 'jazz' })`), these descriptions replace the defaults above. Two cases for
 * v1.5e — minor subdominant (iv) and ♭VII — are the jazz workhorse
 * borrowings and benefit from idiomatic phrasing. Other parallel-mode entries
 * fall through to the defaults.
 */
const JAZZ_OVERRIDES: Record<string, string> = {
  'iv|parallel-mode': 'Minor subdominant — jazz-typical borrowing.',
  '♭VII|parallel-mode': 'Borrowed from Mixolydian — jazz/rock idiom.',
}

/**
 * Optional style hint for `describeChord`. Mirrors `AnalysisContext.style`.
 * Currently only `'jazz'` activates an override table; `'blues'` keeps the
 * defaults (the blues description-swap for I7 / IV7 lives in `analyzeChord`
 * because it's gated on dom7 *and* chord function, not just function).
 */
export type DescribeStyle = 'blues' | 'jazz'

export interface DescribeOptions {
  style?: DescribeStyle
}

export const describeChord = (
  { chordFunction, borrowing, isDominantSeventh, modalSource }: DescribeInput,
  options: DescribeOptions = {}
): string => {
  if (modalSource) {
    return `Borrowed from ${modalSource.tonic} ${MODE_DISPLAY_NAMES[modalSource.mode]}.`
  }
  const key = `${chordFunction}|${borrowing ?? 'null'}`
  const dominantSeventhDescription = isDominantSeventh ? DOMINANT_SEVENTH_TEMPLATES[key] : undefined
  if (dominantSeventhDescription) {
    return dominantSeventhDescription
  }
  if (options.style === 'jazz') {
    const jazzDescription = JAZZ_OVERRIDES[key]
    if (jazzDescription) {
      return jazzDescription
    }
  }
  return TEMPLATES[key] ?? 'Outside the key.'
}
